<template>
  <div class="navbar">
    <a href="/" class="navbar-brand-link" title="StockCharts.com">
      <img src="//d.stockcharts.com/img/scc-logo-light.png" alt="StockCharts Logo" class="logo-full" />
      <img src="//d.stockcharts.com/img/scc-logo-icon.png" alt="StockCharts Icon" class="logo-icon" />
    </a>
    <div class="user-menu">
      <UserDropdownMenu />
    </div>
    <ul class="navbar-account-links">
      <li class="navbar-account-link" v-show="!isLoggedIn">
        <a href="/login/" class="sc3-button sc3-button-transparent navbar-button">
          Log In
        </a>
      </li>
      <li class="navbar-account-link" v-show="!isLoggedIn">
        <a href="/checkout/freetrial/" class="sc3-button sc3-button-light-green navbar-button">
          Free Trial
        </a>
      </li>
      <li class="navbar-account-link" v-show="isLoggedIn">
        <MemberDropdownMenu />
      </li>
    </ul>
    <form
      class="chart-search-form"
      @submit.prevent="handleChartSearchFormSubmit()"
    >
      <div class="chart-search-menu">
        <ChartSearchDropdownMenu v-model:selected-charting-tool="currentChartingTool" />
      </div>
      <div class="chart-search-input-wrapper">
        <input
          type="text"
          class="chart-search-input"
          placeholder="Enter Symbol or Name"
          autocomplete="off"
          v-model="symbol"
          @blur="handleChartSearchInputBlur()"
          @keydown.enter.prevent="formSubmit()"
          @keyup="handleKeyUpChartSearchInput($event)"
          ref="chartSearchInputElement"
        />
        <div
          v-if="suggestions.length && searchQuery"
          class="autosuggest"
        >
          <table class="suggestions">
            <tr
              v-for="(suggestion, suggestionIndex) in suggestions"
              :key="suggestionIndex"
              class="suggestion"
              :class="{
                'even': suggestionIndex % 2 === 0,
                'odd': suggestionIndex % 2 === 1,
                'selected': suggestionIndex === selectedSuggestionIndex
              }"
              @click="selectSuggestion(suggestionIndex)"
            >
              <td class="symbol" v-html="suggestion.highlightedSymbol"></td>
              <td class="company" v-html="suggestion.highlightedCompany"></td>
              <td class="exchange">{{ suggestion.exchange }}</td>
            </tr>
            <tr
              class="suggestion"
              v-if="suggestions.length >= MAX_AUTO_SUGGEST_RESULTS"
              :class="{
                'even': suggestions.length % 2 === 0,
                'odd': suggestions.length % 2 === 1,
                'selected': suggestions.length === selectedSuggestionIndex
              }"
              @click="selectSuggestion(suggestions.length)"
            >
              <td class="more" colspan="3">More Results For "{{ searchQuery }}" ...</td>
            </tr>
          </table>
        </div>
      </div>
      <span class="chart-search-submit-button-container">
        <button
          type="submit"
          class="chart-search-submit-button"
        >Go</button>
      </span>
    </form>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { useChartStore } from '@/stores/chartStore';
  import { useSavedChartsStore } from '@/stores/savedChartsStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useSymbolSuggestions } from '@/composables/useSymbolSuggestions';
  import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
  import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
  import { isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
  import ChartSearchDropdownMenu from '@/components/dropdowns/ChartSearchDropdownMenu.vue';
  import MemberDropdownMenu from '@/components/dropdowns/MemberDropdownMenu.vue';
  import UserDropdownMenu from '@/components/dropdowns/UserDropdownMenu.vue';
  import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';
  import { CHARTING_TOOL_OPTION_SHARP_CHARTS, CHARTING_TOOL_OPTION_POINT_AND_FIGURE, DEFAULT_CHARTING_TOOL_OPTION } from '@/enums/charting-tool-options';

  const chartStore = useChartStore();
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();
  const { getSuggestions, getHighlightedHtmlFromComparison } = useSymbolSuggestions();
  const { loadChartStyle, loadPlatformDefaultChartStyle } = useLoadChartsAndChartStyles();
  const { MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS, getPerfChartSymbols } = usePerfChartSymbols();
  const MAX_AUTO_SUGGEST_RESULTS = 10;
  const symbol = ref('');
  const chartSearchInputElement = ref(null);
  const currentChartingTool = ref(DEFAULT_CHARTING_TOOL_OPTION);
  const trimmedSymbol = computed(() => {
    return typeof symbol.value === 'string' ? symbol.value.trim() : '';
  });
  let lastTypedChartSearchText = '';
  let autoSuggestionsTimerId = 0;
  const selectedSuggestionIndex = ref(-1);
  const searchQuery = ref('');
  const suggestions = ref([]);

  async function handleChartSearchFormSubmit() {
    if (!trimmedSymbol.value) {
      return;
    }

    const encodedTrimmedSymbol = trimmedSymbol.value.replace(/&/g, '%26');

    let url = currentChartingTool.value.url;

    if (currentChartingTool.value.value === CHARTING_TOOL_OPTION_SHARP_CHARTS.value) {
      await changeSC3WorkbenchSymbol();
      destroySuggestions();

      symbol.value = '';

      chartSearchInputElement.value?.blur();
      return;
    }

    if (currentChartingTool.value.value === CHARTING_TOOL_OPTION_POINT_AND_FIGURE.value) {
      url += encodedTrimmedSymbol.toLowerCase();
      url += ',P';
    } else {
      url += encodedTrimmedSymbol;
    }

    window.location = url;
  }

  async function changeSC3WorkbenchSymbol() {
    const formattedTrimmedSymbol = trimmedSymbol.value
      .replace(/\s/g, '')
      .toUpperCase();

    if (!formattedTrimmedSymbol) {
      return;
    }

    let perfChartSymbols = getPerfChartSymbols(formattedTrimmedSymbol).slice(0, MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS);
    const mainChartSymbol = perfChartSymbols.at(0)?.symbol ?? '';
    perfChartSymbols = perfChartSymbols.slice(1);

    if (perfChartSymbols.length) {
      chartStore.$patch({
        symbol: mainChartSymbol,
        chartType: CHART_TYPE_OPTION_PERF_CHART.value,
        perfChartSymbols,
        latestLoadPerfChartTimeStamp: Date.now()
      });
      return;
    }

    if (!isLoggedIn.value) {
      await loadPlatformDefaultChartStyle(mainChartSymbol);
      return;
    }

    if (isValidChartStyleRecid(savedChartsStore.recidOfDefaultChartStyle, false)) {
      await loadChartStyle(savedChartsStore.recidOfDefaultChartStyle, mainChartSymbol);
      return;
    }

    chartStore.$patch({ symbol: mainChartSymbol });
  }

  function handleChartSearchInputBlur() {
    window.setTimeout(() => {
      destroySuggestions();
    }, 500);
  }

  function handleKeyUpChartSearchInput(e) {
    const lastChar = trimmedSymbol.value[trimmedSymbol.value.length - 1];
    const isComma = e.key === ',' || lastChar === ',';
    const isSpace = e.key === 'Tab' || e.key === ' ' || lastChar === ' ';
    const isColon = lastChar === ':';

    if (e.key === 'ArrowDown') {
      next();
      return;
    }

    if (e.key === 'ArrowUp') {
      prev();
      return;
    }

    if (e.key === 'Enter') {
      return;
    }

    lastTypedChartSearchText = trimmedSymbol.value;

    if (isComma || isSpace || isColon) {
      return;
    }

    if (!trimmedSymbol.value) {
      destroySuggestions();
      return;
    }

    window.clearTimeout(autoSuggestionsTimerId);

    autoSuggestionsTimerId = window.setTimeout(() => {
      refreshSuggestions();
    }, 250);
  }

  async function refreshSuggestions() {
    if (!trimmedSymbol.value) {
      destroySuggestions();
      return;
    }

    let formattedTrimmedSymbol = trimmedSymbol.value.toUpperCase();
    const iComma = formattedTrimmedSymbol.lastIndexOf(',');
    const iColon = formattedTrimmedSymbol.lastIndexOf(':');
    const hasComma = iComma !== -1 && iComma > iColon;
    const hasColon = iColon !== -1 && iColon > iComma;

    if (hasComma) {
      formattedTrimmedSymbol = formattedTrimmedSymbol.substring(iComma + 1).trim();
    } else if (hasColon) {
      formattedTrimmedSymbol = formattedTrimmedSymbol.substring(iColon + 1).trim();
    }

    const fetchedSuggestions = await getSuggestions(formattedTrimmedSymbol);
    if (fetchedSuggestions.length)
      showSuggestions(formattedTrimmedSymbol, fetchedSuggestions);
    else
      destroySuggestions();
  }

  function showSuggestions(formattedTrimmedSymbol, formattedSuggestions) {
    if (!formattedSuggestions.length) {
      destroySuggestions();
      return;
    }

    selectedSuggestionIndex.value = -1;
    searchQuery.value = formattedTrimmedSymbol;
    suggestions.value = getHighlightedHtmlFromComparison(formattedTrimmedSymbol, formattedSuggestions);
  }

  async function selectSuggestion(suggestionIndex) {
    selectedSuggestionIndex.value = suggestionIndex;

    fillSearchBox();
    await formSubmit();
  }

  function destroySuggestions() {
    selectedSuggestionIndex.value = -1;
    searchQuery.value = '';
    suggestions.value = [];
  }

  function next() {
    if (
      !suggestions.value.length ||
      selectedSuggestionIndex.value >= suggestions.value.length
    ) {
      selectedSuggestionIndex.value = -1;
    } else {
      selectedSuggestionIndex.value++;
    }

    fillSearchBox();
  }

  function prev() {
    if (!suggestions.value.length) {
      selectedSuggestionIndex.value = -1;
    } else if (selectedSuggestionIndex.value >= 0) {
      selectedSuggestionIndex.value--;
    } else {
      selectedSuggestionIndex.value = suggestions.value.length;
    }

    fillSearchBox();
  }

  async function formSubmit() {
    if (!trimmedSymbol.value) {
      return;
    }

    const formattedTrimmedSymbol = trimmedSymbol.value.toLowerCase();
    const isShowMore = !!suggestions.value.length && selectedSuggestionIndex.value === suggestions.value.length;

    destroySuggestions();

    if (isShowMore) {
      window.location = `/search/?section=symbol&q=${formattedTrimmedSymbol}`;
      return;
    }

    await handleChartSearchFormSubmit();
  }

  function fillSearchBox() {
    if (
      selectedSuggestionIndex.value === -1 ||
      !suggestions.value[selectedSuggestionIndex.value]
    ) {
      symbol.value = lastTypedChartSearchText;
      return;
    }

    const suggestedSymbol = suggestions.value[selectedSuggestionIndex.value].symbol;
    const iComma = trimmedSymbol.value.lastIndexOf(',');
    const iColon = trimmedSymbol.value.lastIndexOf(':');
    const hasComma = iComma !== -1 && iComma > iColon;
    const hasColon = iColon !== -1 && iColon > iComma;

    if (hasComma) {
      const symbols = trimmedSymbol.value.split(',');

      symbols.pop();
      symbols.push(suggestedSymbol);

      symbol.value = symbols.join(',');
      return;
    }

    if (hasColon) {
      const symbols = trimmedSymbol.value.split(':');

      symbols.pop();
      symbols.push(suggestedSymbol);

      symbol.value = symbols.join(':');
      return;
    }

    symbol.value = suggestedSymbol;
  }
</script>

<style scoped>
  .navbar {
    color: white;
  }

  .navbar-brand-link {
    float: left;
    text-decoration: none;
  }

  .logo-full {
    display: block;
    height: 40px;
  }

  .logo-icon {
    display: none;
    height: 35px;
  }

  .user-menu {
    float: right;
    margin: 5px 0px 0px 15px;
    font-size: 0px;
    position: relative;
  }

  .navbar-account-links {
    float: right;
    list-style: none;
    padding: 0px;
    position: relative;
  }

  .navbar-account-link {
    display: inline-block;
    vertical-align: middle;
    margin-bottom: 5px;
  }

  .navbar-account-link:not(:first-of-type) {
    margin-left: 15px;
  }

  .navbar-account-link .sc3-button {
    display: block;
    border-radius: 4px;
    padding: 6px 15px 0px 15px;
    line-height: 21px;
    height: 35px;
    margin: 5px 0px 0px 0px;
  }

  .chart-search-form {
    display: flex;
    vertical-align: middle;
    margin: 0px auto;
    padding: 0px 90px;
    outline-width: 0px;
    border-collapse: separate;
    max-width: 1199px;
    white-space: nowrap;
  }

  .chart-search-menu {
    border-collapse: separate;
    white-space: nowrap;
    vertical-align: middle;
  }

  .chart-search-input-wrapper {
    position: relative;
    flex: 1;
  }

  .chart-search-input {
    font-style: normal;
    color: black;
    border: none;
    font-weight: 900;
    padding: 6px 0px 6px 10px;
    border-radius: 0px;
    height: 40px;
    font-size: 16px;
    display: block;
    width: 100%;
    margin: 0px;
    background-color: white;
    transition: border-color ease-in-out .15s,box-shadow ease-in-out .15s;
  }

  .chart-search-input::placeholder {
    padding-top: 0px;
    text-transform: none;
    font-weight: normal;
    color:#707f8f;
  }

  .chart-search-submit-button-container {
    border-collapse: separate;
    vertical-align: middle;
    white-space: nowrap;
  }

  .chart-search-submit-button {
    color: #707f8f;
    font-weight: 700;
    font-size: 16px;
    padding: 0px 10px;
    background-color: white;
    transition: all 0.35s ease;
    height: 40px;
    border: none;
    margin: 0px 0px 0px -1px;
    touch-action: manipulation;
    user-select: none;
    border-radius: 0px 4px 4px 0px;
  }

  .chart-search-submit-button:hover,
  .chart-search-submit-button:active,
  .chart-search-submit-button:focus {
    color: #3281d0;
  }

  @media (max-width: 1199px) {
    .chart-search-form {
      padding: 0 30px;
    }
  }

  @media (max-width: 991px) {
    .logo-full {
      height: 35px;
      margin-top: 5px;
    }
  }

  @media (max-width: 767px) {
    .logo-full {
      display: none;
    }

    .logo-icon {
      display: block;
    }

    .navbar-account-link:not(:first-of-type) {
      margin-left: 10px;
    }

    .navbar-account-link .sc3-button {
      padding-left: 10px;
      padding-right: 10px;
      margin-top: 0px;
    }

    .user-menu {
      margin-top: 1px;
    }

    .chart-search-form {
      clear: both;
      padding: 5px 0px 0px 0px;
    }
  }

  .autosuggest {
    position: absolute;
    top: 40px;
    left: 0px;
    width: 100%;
    max-width: 100%;
    z-index: 999;
    font-size: 12px;
  }

  .suggestions {
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
    cursor: pointer;
    border: 1px solid #707f8f;
    background-color: white;
    margin: 5px 0px;
    text-align: left;
    color: black;
  }

  .suggestion {
    background-color: white;
  }

  .suggestion.even {
    background-color: #f2f5f7;
  }

  .suggestion:hover {
    background-color: #d0dbe2;
  }

  .suggestion.selected {
    background-color: #707f8f;
  }

  .suggestion td {
    padding: 2px;
  }

  .suggestion .exchange {
    font-size: 10px;
    color: #707f8f;
    text-align: right;
  }

  .suggestion .more {
    font-style: italic;
    color: #707f8f;
  }

  .navbar-button {
    font-size: 14px;
  }
</style>
